import OrderItem from '~/models/order';
import type { Ref } from 'vue';
import type { OrderAdditionalDataInterface } from '~/constants/interfaces/order-additional-data';
import { useConfigStore } from '~/store/config';
import { pushToDataLayer } from '~/composables/useTracking';
import { useCartStore } from '~/store/cart';
import type { OrderItemType } from '~/constants/types/order-item';
import useApiFetch from '~/composables/useApiFetch';
import useFacebookConversion from '~/composables/useFacebookConversion';
import useAdTractionConversion from '~/composables/useAdTractionConversion';

export type PaymentResultResponse = {
  token: string,
  order: OrderItemType,
}

let unifaunResponse: any = null;

export default function useOrderComplete() {
  const orderAdditionalData = useCookie<OrderAdditionalDataInterface | null>('orderAdditionalData', { maxAge: 3600 * 24 * 182, path: '/' });
  const { unifaunPost, apiPost } = useApiFetch();
  const runtimeConfig = useRuntimeConfig();
  const config = useConfigStore();
  const cartStore = useCartStore();
  const { sendPurchaseConversion } = useFacebookConversion();
  const { sendPurchaseConversion: sendAdTractionPurchaseConversion } = useAdTractionConversion();
  const { sellerCookieOptions } = useCookieConfig();
  const isSeller = useCookie<boolean>('isSeller', sellerCookieOptions) as Ref<boolean>;

  /**
   * After order is placed, this creates the unifaun shipping
   */
  const createPreparedShipment = async(order: OrderItem) => {
    if (orderAdditionalData.value) {
      console.log('createPreparedShipment');
      console.log(orderAdditionalData.value);

      const postData = {
        shipment: {
          receiver: {
            name: `${order.shippingAddress.firstName} ${order.shippingAddress.lastName}`,
            address1: `${order.shippingAddress.address1}`,
            zipcode: `${order.shippingAddress.zipCode}`,
            city: `${order.shippingAddress.city}`,
            country: `${order.shippingAddress.country}`,
            phone: `${order.shippingAddress.phoneNumber}`,
            email: `${order.shippingAddress.email}`,
          },
          agent: {} as undefined | object,
          orderNo: order.orderId,
          senderReference: orderAdditionalData.value.preparedId,
        },
        selectedOptionId: orderAdditionalData.value.shippingOptions?.selectedOptionId,
        selectedAddons: orderAdditionalData.value?.shippingOptions?.selectedAddons ?? [],
        returnShipmentData: true,
        prepareId: orderAdditionalData.value.preparedId,
        language: config.market.language,
      };
      if (orderAdditionalData.value?.shippingOptions?.selectedAgent) {
        postData.shipment.agent = {
          quickId: orderAdditionalData.value?.shippingOptions?.selectedAgent,
        };
      } else {
        delete postData.shipment.agent;
      }
      // Fix for empty phone number
      const notifySmsAddon = postData.selectedAddons?.find(
        (a) => a.id === 'FCNOTIFYSMS'
      );
      if (
        notifySmsAddon &&
        (notifySmsAddon.fields?.length === 0 ||
          notifySmsAddon.fields?.find((f) => f.value?.length === 0))
      ) {
        if (order?.shippingAddress?.phoneNumber) {
          postData.selectedAddons = [
            {
              id: 'FCNOTIFYSMS',
              price: 0,
              fields: [
                {
                  id: 'sms',
                  value: order?.shippingAddress?.phoneNumber,
                },
              ],
            },
          ];
        }
      }
      // Fix for Bring in finland which needs this selectedAddon (not documented in unifaun anywhere)
      if (
        orderAdditionalData.value?.shippingOptions?.selectedOptionId ===
        '4892c80f-3b88-41e6-9e9f-deb13e5d3fc2'
      ) {
        postData.selectedAddons.push({ id: 'PUPOPT' });
      }
      let res = {} as any;
      console.log('postdata: '+JSON.stringify(postData));
      try {
        res = await unifaunPost(
          `/api/unifaun/delivery-checkouts/${runtimeConfig.public.unifaunId}`,
          postData
        );
        unifaunResponse = res;
        console.log('unifaun response: '+JSON.stringify(res));

      } catch (e: any) {
        console.error(e);
        res.data = {
          response: e.response,
          request: e.request,
          message: e.message,
          config: e.config,
        };
      }

      try {
        const shipmentData = Object.assign(
          { response: res.data },
          { request: postData }
        );
        await apiPost('/wp-json/api/v1/orders', {
          prepared_shipment_id: orderAdditionalData.value?.preparedId,
          prepared_shipment_data: shipmentData,
        });
      } catch (e: any) {
        console.error(e);
      }
      return res;
    } else {
      console.log('No data to send to Unifaun');
      return null;
    }
  };

  const emptyAditionalDatacookie = () => {
    /**
     * Delete cookie
     * For debugging just comment this row and it will send on every reload on thank you page
     */
    console.log('Empty orderAdditionalData cookie');
    orderAdditionalData.value = null;
  };

  /**
   * Tracking
   */
  const sendGtmReceipt = (order: OrderItem) => {
    if (order && orderAdditionalData.value) {
      const items = order.items.map((item) => {
        return {
          id: item.sku,
          feedId: item.sku,
          ...item.product.gtmProductValues,
          quantity: item.quantity,
        };
      });

      let eventName = '';
      if (isSeller.value) {
        eventName = 'seller_purchase';
      } else {
        eventName = 'purchase';
      }
      
      // Calculate order value excluding tax (for GTM tracking)
      const revenueNum = order.totalPriceAsNumber;
      const taxNum = order.taxAddedAsNumber;
      const revenueExTax = revenueNum - taxNum;
      
      const payload = {
        event: eventName,
        email: order.order?.shippingAddress?.email,
        ecommerce: {
          currencyCode: cartStore.cartData.currency,
          purchase: {
            actionField: {
              id: order.orderId,
              order_id: order.orderId, // GTM-friendly field name
              revenue: order.totalPrice.replace(/\s/g, ''),
              revenue_ex_tax: revenueExTax.toString(), // Pre-calculated value without tax
              order_value_ex_tax: revenueExTax, // Numeric value for easy GTM access
              tax: order.taxAddedAsNumber.toString().replace(/\s/g, ''),
              shipping: order.shippingPriceAsNumber
                .toString()
                .replace(/\s/g, ''),
              coupon: order.voucher,
            },
            products: items,
          },
        },
      };
      console.log('payload: ' + JSON.stringify(payload));
      pushToDataLayer(payload);
    } else {
      console.warn('No order');
    }
  };

  const setNewsletter = () => {
    const hasAcceptedNewsletter =
      sessionStorage.getItem('acceptedNewsletter');

    const newsletterIsAccepted = hasAcceptedNewsletter
      ? JSON.parse(hasAcceptedNewsletter)
      : '';

    if (newsletterIsAccepted) {
      pushToDataLayer({
        event: 'email_opt_in',
        location: 'checkout',
      });
    }
  };

  const updateAdditionalOrderInfo = async(orderId: string, unifaunResponse: any, thankYouText: string, groupName: string = '', sellerInfo: string = '', sellerId: string = '') => {
    let agentText = null;
    let serviceText = null;
    
    // Parse and extract Unifaun data if present
    if (unifaunResponse) {
      // Parse if it's a stringified JSON
      let parsedResponse = unifaunResponse;
      if (typeof unifaunResponse === 'string') {
        try {
          parsedResponse = JSON.parse(unifaunResponse);
        } catch (e) {
          console.error('Failed to parse Unifaun response:', e);
        }
      }
      
      // Format agent data as plain text: name<br>address1<br>zipcode city
      agentText = parsedResponse?.agent ? 
        `${parsedResponse.agent.name}<br>${parsedResponse.agent.address1}<br>${parsedResponse.agent.zipcode} ${parsedResponse.agent.city}` 
        : null;
      
      // Extract service title as plain text
      serviceText = parsedResponse?.service?.title || null;
    }
    
    // Only proceed if we have at least one field to update
    if (!agentText && !serviceText && !thankYouText && !groupName && !sellerInfo && !sellerId) {
      console.log('No data to update on order');
      return;
    }
    
    // Send as plain text strings via GET query params
    const params = new URLSearchParams();
    params.append('id', orderId);
    if (agentText) params.append('unifaunagent', agentText);
    if (serviceText) params.append('unifaunservice', serviceText);
    if (thankYouText) params.append('thankyoutext', thankYouText);
    if (groupName) params.append('groupname', groupName);
    if (sellerInfo) params.append('sellerinfo', sellerInfo);
    if (sellerId) params.append('sellerid', sellerId);
    
    const res = await $fetch(`/api/updateOrder?${params.toString()}`);
    console.log('Order updated with additional info');
    console.log(JSON.stringify(res));
  };

  /**
   * Send Facebook conversion tracking for purchase
   */
  const sendFacebookPurchaseConversion = async(order: OrderItem) => {
    if (orderAdditionalData.value) {
      if (isSeller.value) {
        // dont send for seller link orders
        console.log('Dont send fb purchase for seller link orders');
      } else {
        try {
          await sendPurchaseConversion(order);
          console.log('Facebook purchase conversion sent successfully');
        } catch (error) {
          console.error('Failed to send Facebook purchase conversion:', error);
          // Silent fail like the original implementation
        }
      }
    }
  };

  /**
   * Send AdTraction conversion tracking for purchase
   */
  const sendAdTractionConversion = async(order: OrderItem) => {
    if (orderAdditionalData.value) {
      if (isSeller.value) {
        // dont send for seller link orders
        console.log('Dont send AdTraction purchase for seller link orders');
      } else {
        try {
          await sendAdTractionPurchaseConversion(order);
        } catch (error) {
          console.error('Failed to send AdTraction purchase conversion:', error);
          // Silent fail like the Facebook implementation
        }
      }
    }
  };

  return {
    createPreparedShipment,
    emptyAditionalDatacookie,
    sendGtmReceipt,
    setNewsletter,
    updateAdditionalOrderInfo,
    sendFacebookPurchaseConversion,
    sendAdTractionConversion,
  };

}
